import { Injectable } from '@angular/core'
import { HttpClient } from '@angular/common/http'
import { BehaviorSubject } from 'rxjs'

import { environment } from '../../../../environments/environment'
import { SyncOperation, SyncStatus } from '../../interfaces/sync-operation.interface'
import { OfflineCacheService } from './offline-cache.service'

@Injectable({
  providedIn: 'root'
})
export class SyncQueueService {
  pendingCount$: BehaviorSubject<number> = new BehaviorSubject<number>(0)
  isSyncing$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false)
  failedCount$: BehaviorSubject<number> = new BehaviorSubject<number>(0)

  tempIdMap: Map<string, number> = new Map()
  private processing = false

  constructor(
    private http: HttpClient,
    private cacheService: OfflineCacheService
  ) {}

  private async persistTempIdMap(): Promise<void> {
    const obj: { [key: string]: number } = {}
    this.tempIdMap.forEach((value, key) => obj[key] = value)
    await this.cacheService.put('meta', { id: 'tempIdMap', value: obj })
  }

  private async loadTempIdMap(): Promise<void> {
    try {
      const stored = await this.cacheService.get<{ id: string, value: { [key: string]: number } }>('meta', 'tempIdMap')
      if (stored && stored.value) {
        Object.keys(stored.value).forEach(key => {
          this.tempIdMap.set(key, stored.value[key])
        })
      }
    } catch (e) {
      // No persisted map — fine
    }
  }

  async enqueue(op: {
    entityType: 'sample' | 'weighing',
    operationType: 'create' | 'update' | 'delete',
    entityId: number | string,
    payload: { [key: string]: any },
    photoKeys: string[]
  }): Promise<void> {
    const now = Date.now()
    const syncOp: SyncOperation = {
      ...op,
      status: 'pending' as SyncStatus,
      retryCount: 0,
      createdAt: now,
      updatedAt: now
    }
    await this.cacheService.put('sync_queue', syncOp)
    await this.updateCounts()
  }

  async processQueue(): Promise<void> {
    if (this.processing) {
      return
    }

    this.processing = true
    this.isSyncing$.next(true)

    try {
      await this.loadTempIdMap()
      const allOps = await this.cacheService.getAll<SyncOperation>('sync_queue')
      const ops = allOps
        .filter(op =>
          op.status === 'pending' ||
          op.status === 'in_progress' ||
          (op.status === 'failed' && op.retryCount < 3)
        )
        .sort((a, b) => a.createdAt - b.createdAt)

      for (const op of ops) {
        // Mark in_progress
        op.status = 'in_progress'
        op.updatedAt = Date.now()
        await this.cacheService.put('sync_queue', op)

        try {
          await this.processOperation(op)
          op.status = 'completed'
          op.updatedAt = Date.now()
          await this.cacheService.put('sync_queue', op)
        } catch (err) {
          op.status = 'failed'
          // Only count retries for real server errors (4xx/5xx).
          // Network errors (status 0 or no status) are transient and safe to retry
          // indefinitely thanks to server-side idempotency keys.
          if (err && err.status && err.status > 0) {
            op.retryCount++
          }
          op.lastError = err.message || err.statusText || 'Unknown error'
          op.updatedAt = Date.now()
          await this.cacheService.put('sync_queue', op)
        }
      }
    } finally {
      this.processing = false
      this.isSyncing$.next(false)
      await this.updateCounts()
    }

    // Clean completed operations
    await this.cleanCompleted()

    // Reconcile ghost entities (cache entries still flagged _pendingSync or
    // with a temp id but no matching active op — can happen if cache cleanup
    // after a successful server commit failed and was swallowed).
    await this.reconcileGhostEntities()
  }

  /**
   * Walks the samples/weighings caches and removes "ghost" entries: cache rows
   * that still look in-progress (_pendingSync === true or id starting with
   * "temp_") but have no corresponding active operation in sync_queue. Ghosts
   * appear when the server accepted an op but the subsequent local cache write
   * failed — the op is completed and gone, yet the UI keeps showing a pending
   * badge on the row.
   *
   * Temp-id rows are deleted outright (the server version lives under the real
   * id, not this temp one). Real-id rows just lose their _pendingSync flag.
   */
  async reconcileGhostEntities(): Promise<void> {
    const allOps = await this.cacheService.getAll<SyncOperation>('sync_queue')
    const activeSampleIds = new Set<string>()
    const activeWeighingIds = new Set<string>()
    for (const op of allOps) {
      if (op.status === 'completed') {
        continue
      }
      const idStr = String(op.entityId)
      if (op.entityType === 'sample') {
        activeSampleIds.add(idStr)
      } else if (op.entityType === 'weighing') {
        activeWeighingIds.add(idStr)
      }
    }

    await this.reconcileStore('samples', activeSampleIds)
    await this.reconcileStore('weighings', activeWeighingIds)
  }

  private async reconcileStore(store: 'samples' | 'weighings', activeIds: Set<string>): Promise<void> {
    const rows = await this.cacheService.getAll<any>(store)
    for (const row of rows) {
      if (!row || row.id === undefined || row.id === null) {
        continue
      }
      const idStr = String(row.id)
      const isTemp = idStr.startsWith('temp_')
      const hasPendingFlag = row._pendingSync === true
      if (!isTemp && !hasPendingFlag) {
        continue
      }
      if (activeIds.has(idStr)) {
        continue
      }
      try {
        if (isTemp) {
          await this.cacheService.delete(store, row.id)
        } else {
          delete row._pendingSync
          await this.cacheService.put(store, row)
        }
      } catch (e) {
        console.warn('Reconcile ghost entity failed:', e)
      }
    }
  }

  async cleanCompleted(): Promise<void> {
    const allOps = await this.cacheService.getAll<SyncOperation>('sync_queue')
    for (const op of allOps) {
      if (op.status === 'completed') {
        await this.cacheService.delete('sync_queue', op.id)
      }
    }
  }

  async getPendingForEntity(entityType: string, entityId: number | string): Promise<SyncOperation[]> {
    const allOps = await this.cacheService.getAll<SyncOperation>('sync_queue')
    return allOps.filter(
      op => op.entityType === entityType &&
        String(op.entityId) === String(entityId) &&
        op.status !== 'completed'
    )
  }

  async getAllPending(): Promise<SyncOperation[]> {
    const allOps = await this.cacheService.getAll<SyncOperation>('sync_queue')
    return allOps.filter(op => op.status !== 'completed')
  }

  private async processOperation(op: SyncOperation): Promise<void> {
    const formData = await this.reconstructFormData(op)
    const resolvedEntityId = this.resolveId(op.entityId)

    switch (op.operationType) {
      case 'create': {
        // Deduplication: if this temp ID was already synced (e.g. server received
        // the POST but the response was lost due to network cut), skip the create.
        if (typeof op.entityId === 'string' && op.entityId.startsWith('temp_') && this.tempIdMap.has(op.entityId)) {
          await this.safeCacheCleanup(async () => {
            const store = op.entityType === 'sample' ? 'samples' : 'weighings'
            await this.cacheService.delete(store, op.entityId)
          })
          break
        }

        const url = `${environment.apiBaseUrl}${op.entityType === 'sample' ? 'samples' : 'weighings'}`
        // Server commit — only this call may raise an error that triggers a retry.
        const result = await this.http.post(url, formData).toPromise() as any

        // Past this point the server has accepted the operation. Cache/cleanup
        // errors must never mark the op as failed — the sync is already done.
        await this.safeCacheCleanup(async () => {
          if (typeof op.entityId === 'string' && op.entityId.startsWith('temp_')) {
            this.tempIdMap.set(op.entityId, result.id)
            await this.persistTempIdMap()
          }
          const store = op.entityType === 'sample' ? 'samples' : 'weighings'
          await this.cacheService.delete(store, op.entityId)
          await this.cacheService.put(store, result)
        })
        break
      }
      case 'update': {
        const resourceName = op.entityType === 'sample' ? 'samples' : 'weighings'
        const url = `${environment.apiBaseUrl}${resourceName}/${resolvedEntityId}`
        const result = await this.http.put(url, formData).toPromise() as any

        await this.safeCacheCleanup(async () => {
          const store = op.entityType === 'sample' ? 'samples' : 'weighings'
          await this.cacheService.put(store, result)
        })
        break
      }
      case 'delete': {
        const resourceName = op.entityType === 'sample' ? 'samples' : 'weighings'
        const url = `${environment.apiBaseUrl}${resourceName}/${resolvedEntityId}`
        await this.http.delete(url).toPromise()

        await this.safeCacheCleanup(async () => {
          const store = op.entityType === 'sample' ? 'samples' : 'weighings'
          await this.cacheService.delete(store, op.entityId)
        })
        break
      }
    }
  }

  // Runs cache/cleanup work after a successful server commit. Any failure is
  // logged but swallowed — we must not mark the sync op as failed here, the
  // server already accepted it. Leftover ghost entries are fixed by the
  // post-sync reconciliation pass.
  private async safeCacheCleanup(fn: () => Promise<void>): Promise<void> {
    try {
      await fn()
    } catch (e) {
      console.warn('Cache cleanup after successful sync failed (non-fatal):', e)
    }
  }

  private async reconstructFormData(op: SyncOperation): Promise<FormData> {
    const formData = new FormData()

    // Append payload fields, resolving temp IDs
    for (const key of Object.keys(op.payload)) {
      let value = op.payload[key]
      // Resolve temp IDs in sampleId references
      if (key === 'sampleId' && typeof value === 'string' && value.startsWith('temp_')) {
        value = this.resolveId(value)
      }
      if (value !== null && value !== undefined) {
        // Repeated keys (e.g. userIds) were serialized as an array — re-expand them
        if (Array.isArray(value)) {
          value.forEach(v => formData.append(key, v))
        } else {
          formData.append(key, value)
        }
      }
    }

    // Send idempotency key for create operations to prevent duplicates
    if (op.operationType === 'create' && typeof op.entityId === 'string' && op.entityId.startsWith('temp_')) {
      formData.append('idempotencyKey', op.entityId)
    }

    // Append photos from IndexedDB
    for (const photoKey of op.photoKeys) {
      const parts = photoKey.split('|')
      if (parts.length === 3) {
        const [entityType, entityId, fieldName] = parts
        const blob = await this.cacheService.getPhoto(entityType, entityId, fieldName)
        if (blob) {
          formData.append(fieldName, blob, fieldName + '.jpg')
        }
      }
    }

    return formData
  }

  private resolveId(id: number | string): number | string {
    if (typeof id === 'string' && id.startsWith('temp_')) {
      return this.tempIdMap.get(id) || id
    }
    return id
  }

  async loadPersistedCounts(): Promise<void> {
    await this.cacheService.init()
    await this.loadTempIdMap()
    await this.updateCounts()
  }

  private async updateCounts(): Promise<void> {
    const allOps = await this.cacheService.getAll<SyncOperation>('sync_queue')
    // Pending includes retryable failures (retryCount < 3) — they are not done,
    // they will be reattempted, and hiding them from the user falsely signals
    // "all synced" while work is still queued.
    const pending = allOps.filter(op =>
      op.status === 'pending' ||
      op.status === 'in_progress' ||
      (op.status === 'failed' && op.retryCount < 3)
    ).length
    const failed = allOps.filter(op => op.status === 'failed' && op.retryCount >= 3).length
    this.pendingCount$.next(pending)
    this.failedCount$.next(failed)
  }

  generateTempId(): string {
    const random = Math.floor(1000 + Math.random() * 9000)
    return `temp_${Date.now()}_${random}`
  }
}
